import { useQueries } from "@tanstack/react-query";
import { ArrowLeft, Minus, Package, Plus } from "lucide-react";
import { Fragment, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { isFeatureEnabled } from "@/shared/lib/feature-flags";
import { Badge } from "@/shared/ui/badge";
import { Modal } from "@/shared/ui/modal";

import { bundleExpansionQuery, type BundleComponent } from "../api/use-bundle-expansion";
import type { AttentionReason } from "../api/use-orders";
import { channelName, packageNo, payMethod, payStatusVal } from "../lib/detail";
import { formatBangkokDateTime, money, itemCount, totals } from "../lib/format";
import { MARKETPLACE_STATUS_LABEL } from "../lib/marketplace-status-labels";
import type { Order } from "../lib/types";

import { ExceptionBanner } from "./ExceptionBanner";
import { OrderActivity } from "./OrderActivity";
import { OrderRefreshIndicator } from "./OrderRefreshIndicator";
import { RelatedOrdersBanner } from "./RelatedOrdersBanner";
import { ResolveDialog } from "./ResolveDialog";
import { SettlementCard } from "./SettlementCard";
import { Timeline } from "./Timeline";

// The two post-ingest exception reasons that get an ExceptionBanner — same
// set as AttentionReason/AttentionCounts.by_reason, kept as a literal list
// here because a banner needs ALL applicable reasons, not just the single
// highest-severity one topReviewReason() picks for the table icon.
const EXCEPTION_REASONS: readonly AttentionReason[] = ["oversold", "buyer_cancel_requested"];

interface InfoRowProps {
  label: string;
  value: JSX.Element | string;
}

function InfoRow({ label, value }: InfoRowProps): JSX.Element {
  return (
    <div className="flex items-start gap-3 py-2 text-sm">
      <span className="w-44 shrink-0 text-[var(--text-subtle)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

interface OrderDetailSectionsProps {
  order: Order;
  onBack: () => void;
  /** orders:write — refresh, resolve. Defaults to true. */
  canWrite?: boolean;
}

export function OrderDetailSections({
  order,
  onBack,
  canWrite = true,
}: OrderDetailSectionsProps): JSX.Element {
  const { t } = useTranslation("orders");
  const [resolveReason, setResolveReason] = useState<AttentionReason | null>(null);
  const activeExceptionReasons = EXCEPTION_REASONS.filter((r) => order.reviewReasons?.includes(r));
  const lineTotalSum = order.items.reduce(
    (sum, it) =>
      sum + (it.gift ? 0 : it.total !== undefined ? Number(it.total) : Number(it.unit) * it.qty),
    0,
  );
  const tt = totals(order.money, lineTotalSum);
  const mkp = order.mkp;
  const showSettlement = isFeatureEnabled("settlement_panel");
  const [previewItem, setPreviewItem] = useState<(typeof order.items)[0] | null>(null);
  const [broken, setBroken] = useState<Set<string>>(new Set());

  // sino-parity: expand each bundle/pack line to its base components
  // (base SKU × multiplicity × line qty). One query per bundle row; non-listing
  // rows resolve to [] (no toggle). Keyed by item index so child rows attach.
  const bundleRows = order.items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it.bundle && Boolean(it.sino));
  const expansions = useQueries({
    queries: bundleRows.map(({ it }) => bundleExpansionQuery(it.sino)),
  });
  const componentsByIdx = new Map<number, BundleComponent[]>();
  bundleRows.forEach(({ idx }, i) => componentsByIdx.set(idx, expansions[i]?.data ?? []));
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  function toggleBundle(idx: number): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  return (
    <div className="space-y-5 px-1 py-4">
      {/* Back link */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-[var(--text-subtle)] hover:text-[var(--text-primary)]"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("back_to_orders")}
      </button>

      {/* Hero */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-bold text-[var(--text-primary)]">{order.sale}</span>
        {order.splitIndex !== undefined && order.splitTotal !== undefined && (
          <span className="inline-flex items-center rounded-full bg-[var(--intent-info-bg)] px-2.5 py-0.5 text-xs font-semibold text-[var(--intent-info-fg)]">
            {t("split_of", { n: order.splitIndex, m: order.splitTotal })}
          </span>
        )}
        <div className="ml-auto">
          <OrderRefreshIndicator id={order.id} canRefresh={canWrite} />
        </div>
      </div>

      {activeExceptionReasons.map((reason) => (
        <ExceptionBanner
          key={reason}
          order={order}
          reason={reason}
          onResolve={() => setResolveReason(reason)}
          canWrite={canWrite}
        />
      ))}

      <RelatedOrdersBanner saleOrderId={order.sale} relatedOrderIds={order.relatedOrderIds} />

      {/* Order Info + Customer Info */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Order Information card */}
        <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
          <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
            <h3 className="font-semibold text-[var(--text-primary)]">{t("oi_title")}</h3>
          </div>
          <div className="divide-y divide-[var(--premium-border)] px-5">
            <InfoRow label={t("oi_channel")} value={channelName(mkp)} />
            <InfoRow
              label={t("oi_marketplace_order_no")}
              value={<span className="font-mono">{order.sale}</span>}
            />
            <InfoRow
              label={t("oi_marketplace_status")}
              value={
                order.marketplaceStatus
                  ? (MARKETPLACE_STATUS_LABEL[order.marketplaceStatus.toLowerCase()] ??
                    order.marketplaceStatus)
                  : "—"
              }
            />
            <InfoRow
              label={t("oi_package_no")}
              value={<span className="font-mono">{packageNo(order)}</span>}
            />
            <InfoRow
              label={t("oi_created")}
              value={<span className="font-mono">{formatBangkokDateTime(order.created)}</span>}
            />
            <InfoRow
              label={t("oi_updated")}
              value={<span className="font-mono">{formatBangkokDateTime(order.created)}</span>}
            />
            <InfoRow
              label={t("oi_seller_display")}
              value={
                <span>
                  {order.seller}
                  {order.platformShopName && (
                    <span className="ml-1.5 block text-xs text-[var(--text-subtle)]">
                      {order.platformShopName}
                    </span>
                  )}
                </span>
              }
            />
            <InfoRow label={t("oi_courier")} value={order.courier ?? "—"} />
            <InfoRow
              label={t("oi_tracking")}
              value={<span className="font-mono">{order.tracking ?? "—"}</span>}
            />
            <InfoRow label={t("oi_warehouse")} value={order.warehouse ?? "—"} />
            <InfoRow
              label={t("oi_paid_at")}
              value={<span className="font-mono">{formatBangkokDateTime(order.paidAt)}</span>}
            />
            <InfoRow
              label={t("oi_shipped_at")}
              value={<span className="font-mono">{formatBangkokDateTime(order.shippedAt)}</span>}
            />
            <InfoRow
              label={t("oi_completed_at")}
              value={<span className="font-mono">{formatBangkokDateTime(order.completedAt)}</span>}
            />
          </div>
        </div>

        {/* Customer Information card */}
        <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
          <div className="border-b border-[var(--premium-border)] px-5 py-3">
            <h3 className="font-semibold text-[var(--text-primary)]">{t("ci_title")}</h3>
          </div>
          {order.customer ? (
            <div
              className="divide-y divide-[var(--premium-border)] px-5"
              data-testid="customer-pii"
            >
              <InfoRow label={t("ci_name")} value={order.customer.name || "—"} />
              <InfoRow
                label={t("ci_phone")}
                value={
                  <span className="font-mono" data-testid="customer-phone">
                    {order.customer.phone || "—"}
                  </span>
                }
              />
              <InfoRow
                label={t("ci_email")}
                value={
                  <span className="font-mono" data-testid="customer-email">
                    {order.customer.email || "—"}
                  </span>
                }
              />
              <InfoRow label={t("ci_social")} value={order.customer.social || "—"} />
              <InfoRow label={t("ci_billing")} value={order.customer.billing_name || "—"} />
              <InfoRow
                label={t("ci_ship_addr")}
                value={
                  <span data-testid="customer-shipping-address">
                    {order.customer.shipping_address || "—"}
                  </span>
                }
              />
              <InfoRow label={t("ci_bill_addr")} value={order.customer.billing_address || "—"} />
            </div>
          ) : (
            <div
              className="px-5 py-6 flex flex-col items-center text-center gap-2"
              data-testid="customer-not-provided"
            >
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {t("customer_not_provided")}
              </p>
              <p className="text-xs text-[var(--text-subtle)] max-w-xs">
                {t("customer_not_provided_note")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Items card */}
      <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
        <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
          <h3 className="font-semibold text-[var(--text-primary)]">{t("sec_items")}</h3>
          <span className="text-xs text-[var(--text-subtle)]">
            {itemCount(order.items)} {t("units")}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--premium-border)] text-left text-xs text-[var(--text-subtle)]">
                <th className="px-5 py-2.5 font-medium">{t("pt_image")}</th>
                <th className="px-5 py-2.5 font-medium">{t("pt_sku")}</th>
                <th className="px-5 py-2.5 font-medium">{t("pt_type")}</th>
                <th className="px-5 py-2.5 font-medium">{t("pt_freebies")}</th>
                <th className="px-5 py-2.5 font-medium">{t("pt_product")}</th>
                <th className="px-5 py-2.5 text-right font-medium">{t("pt_qty")}</th>
                <th className="px-5 py-2.5 text-right font-medium">{t("pt_selling")}</th>
                <th className="px-5 py-2.5 text-right font-medium">{t("pt_discount")}</th>
                <th className="px-5 py-2.5 text-right font-medium">{t("pt_total")}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, idx) => {
                // Prefer the BE's own marketplace-aware total (nets discount where
                // the marketplace reports it separately from price, e.g. Shopee
                // escrow/Lazada vouchers) — unit x qty alone under-reports it for
                // those. Falls back to unit x qty only for rows the BE hasn't sent
                // a total for yet (older data, MSW fixtures).
                const lineTotal = item.gift
                  ? 0
                  : item.total !== undefined
                    ? Number(item.total)
                    : Number(item.unit) * item.qty;
                const comps = componentsByIdx.get(idx) ?? [];
                const hasExpansion = comps.length > 0;
                const isExpanded = hasExpansion && !collapsed.has(idx);
                return (
                  <Fragment key={`${item.sino}-${idx}`}>
                    <tr className="border-b border-[var(--premium-border)] last:border-0">
                      <td className="px-5 py-3">
                        {item.imageUrl && !broken.has(`${item.sino}-${idx}`) ? (
                          <button
                            type="button"
                            onClick={() => setPreviewItem(item)}
                            title={t("img_preview_hint")}
                            aria-label={`${t("img_preview_hint")}: ${item.name}`}
                            className="block h-12 w-12 overflow-hidden rounded-md border border-[var(--premium-border)]"
                          >
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              loading="lazy"
                              className="h-full w-full object-cover"
                              onError={() =>
                                setBroken((prev) => new Set(prev).add(`${item.sino}-${idx}`))
                              }
                            />
                          </button>
                        ) : (
                          <span
                            data-testid="img-placeholder"
                            aria-label={t("img_none")}
                            className="flex h-12 w-12 items-center justify-center rounded-md border border-[var(--premium-border)] bg-[var(--intent-neutral-bg)]"
                          >
                            <Package aria-hidden className="h-5 w-5 text-[var(--text-subtle)]" />
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {hasExpansion && (
                            <button
                              type="button"
                              onClick={() => toggleBundle(idx)}
                              aria-expanded={isExpanded}
                              aria-label={t(isExpanded ? "pt_collapse" : "pt_expand")}
                              data-testid={`bundle-toggle-${idx}`}
                              className="grid h-5 w-5 shrink-0 place-items-center rounded border border-[var(--premium-border-strong)] text-[var(--text-subtle)] transition hover:bg-[var(--canvas)]"
                            >
                              {isExpanded ? (
                                <Minus className="h-3 w-3" aria-hidden />
                              ) : (
                                <Plus className="h-3 w-3" aria-hidden />
                              )}
                            </button>
                          )}
                          <span className="font-mono text-xs">{item.merch || item.sino}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center rounded-md bg-[var(--intent-neutral-bg)] px-2 py-0.5 text-xs font-semibold text-[var(--intent-neutral-fg)]">
                          {mkp.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {item.gift ? (
                          <span className="inline-flex items-center rounded-full bg-[var(--intent-success-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--intent-success-fg)]">
                            {t("tag_gift")}
                          </span>
                        ) : (
                          <span className="text-[var(--text-subtle)]">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span>{item.name}</span>
                        {item.bundle && (
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-[var(--intent-info-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--intent-info-fg)]">
                            {t("tag_combo")}
                          </span>
                        )}
                        {item.unmapped && (
                          <span
                            data-testid="item-unmapped"
                            className="ml-1.5 inline-flex items-center rounded-full bg-[var(--intent-warning-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--intent-warning-fg)]"
                          >
                            {t("tag_unmapped")}
                          </span>
                        )}
                      </td>
                      <td
                        className={
                          "px-5 py-3 text-right font-mono" +
                          (item.qty >= 2 ? " text-[var(--intent-info-fg)] font-semibold" : "")
                        }
                      >
                        {item.qty}
                      </td>
                      {(() => {
                        // A gift's price is permanently ฿0 by definition — it never
                        // "awaits settlement" the way a real item's provisional price does.
                        const hasPrice = item.gift || Number(item.unit) > 0 || order.settled;
                        if (!hasPrice) {
                          return (
                            <>
                              <td className="px-5 py-3 text-right">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right" data-testid="item-awaiting">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                            </>
                          );
                        }
                        return (
                          <>
                            <td className="px-5 py-3 text-right font-mono">
                              <span className="inline-flex items-center gap-1">
                                {money(item.gift ? 0 : item.unit, order.currency)}
                                {!item.gift && !order.settled && (
                                  <span
                                    data-testid="item-price-unconfirmed"
                                    title={t("unconfirmed_price_tooltip")}
                                    className="inline-flex h-1.5 w-1.5 rounded-full bg-[var(--intent-warning-fg)]"
                                  />
                                )}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-right font-mono">
                              {item.discount !== undefined
                                ? money(item.discount, order.currency)
                                : "—"}
                            </td>
                            <td className="px-5 py-3 text-right font-mono font-semibold">
                              {money(lineTotal, order.currency)}
                            </td>
                          </>
                        );
                      })()}
                    </tr>
                    {isExpanded &&
                      comps.map((c, ci) => (
                        <tr
                          key={`${item.sino}-${idx}-base-${ci}`}
                          data-testid={`bundle-base-row-${idx}-${ci}`}
                          className="border-b border-[var(--premium-border)] bg-[var(--canvas)] text-[var(--text-secondary)] last:border-0"
                        >
                          <td className="px-5 py-2" />
                          <td className="px-5 py-2 pl-10">
                            <span className="font-mono text-xs">{c.base_sino_sku}</span>
                          </td>
                          <td className="px-5 py-2">
                            <span className="inline-flex items-center rounded-md bg-[var(--intent-neutral-bg)] px-2 py-0.5 text-xs font-semibold text-[var(--intent-neutral-fg)]">
                              {mkp.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-5 py-2 text-[var(--text-subtle)]">—</td>
                          <td className="px-5 py-2">
                            <span>{c.product_name || c.base_sino_sku}</span>
                          </td>
                          <td className="px-5 py-2 text-right font-mono">
                            <span
                              title={t("bundle_qty_breakdown", {
                                multiplicity: c.multiplicity,
                                qty: item.qty,
                              })}
                            >
                              {c.multiplicity * item.qty}
                            </span>
                          </td>
                          {order.settled ? (
                            <>
                              <td className="px-5 py-2 text-right font-mono">
                                {money(0, order.currency)}
                              </td>
                              <td className="px-5 py-2 text-right font-mono">
                                {money(0, order.currency)}
                              </td>
                              <td className="px-5 py-2 text-right font-mono">
                                {money(0, order.currency)}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-5 py-2 text-right">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                              <td className="px-5 py-2 text-right">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                              <td className="px-5 py-2 text-right">
                                <span className="text-[var(--text-subtle)] text-xs">
                                  {t("awaiting_settlement")}
                                </span>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Image preview modal */}
      <Modal
        open={previewItem !== null && !!previewItem?.imageUrl}
        onClose={() => setPreviewItem(null)}
        title={previewItem?.name}
      >
        {previewItem?.imageUrl && (
          <img
            src={previewItem.imageUrl}
            alt={previewItem.name}
            className="max-h-[70vh] w-auto rounded-lg"
          />
        )}
      </Modal>

      {/* Payment Details + Order Totals (+ Settlement) */}
      <div
        className={`grid grid-cols-1 gap-5 ${showSettlement ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}
      >
        {/* Payment Details card */}
        <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
          <div className="border-b border-[var(--premium-border)] px-5 py-3">
            <h3 className="font-semibold text-[var(--text-primary)]">{t("pay_title")}</h3>
          </div>
          <div className="divide-y divide-[var(--premium-border)] px-5">
            <InfoRow label={t("pay_method")} value={payMethod(order.pay, order.payChannel)} />
            <InfoRow label={t("pay_channel")} value="—" />
            <InfoRow
              label={t("pay_status")}
              value={
                <Badge intent={order.paidAt ? "success" : "warning"} dot>
                  {payStatusVal(order)}
                </Badge>
              }
            />
            <InfoRow
              label={t("pay_reference")}
              value={<span className="text-[var(--text-subtle)]">—</span>}
            />
            <InfoRow label={t("pay_currency")} value={order.currency || "THB"} />
          </div>
        </div>

        {/* Order Totals card */}
        <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
          <div className="flex items-center gap-2 border-b border-[var(--premium-border)] px-5 py-3">
            <h3 className="font-semibold text-[var(--text-primary)]">{t("tot_title")}</h3>
          </div>
          <div className="px-5 py-3">
            <div className="space-y-1.5 text-sm">
              <Line
                label={t("tot_sub")}
                value={tt.sub}
                testId="tot-sub"
                currency={order.currency || "THB"}
              />
              <Line
                label={t("tot_discount")}
                value={tt.totalDisc}
                sign="−"
                testId="tot-total-disc"
                currency={order.currency || "THB"}
                danger
              />
              <Line
                label={`· ${t("tot_item_disc")}`}
                value={tt.itemDisc}
                sign="−"
                testId="tot-item-disc"
                currency={order.currency || "THB"}
                sub
              />
              <Line
                label={`· ${t("tot_voucher")}`}
                value={tt.voucher}
                sign="−"
                testId="tot-voucher"
                currency={order.currency || "THB"}
                sub
              />
              <Line
                label={t("tot_shipping")}
                value={tt.shipping}
                sign="+"
                testId="tot-shipping"
                currency={order.currency || "THB"}
              />
              <div className="flex items-center justify-between border-t border-[var(--premium-border)] pt-2 font-semibold">
                <span className="inline-flex items-center gap-1">
                  {t("tot_grand")}
                  {tt.reconciles ? (
                    <span
                      data-testid="tot-reconcile-ok"
                      title={t("tot_reconcile_ok")}
                      className="text-[var(--intent-success-fg)]"
                    >
                      ✓
                    </span>
                  ) : (
                    <span
                      data-testid="tot-reconcile-bad"
                      title={t("tot_mismatch")}
                      className="text-[var(--intent-warning-fg)]"
                    >
                      ⚠
                    </span>
                  )}
                </span>
                <span className="font-mono" data-testid="tot-grand">
                  {money(tt.grand, order.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {showSettlement && <SettlementCard order={order} />}
      </div>

      {/* Timeline + Activity */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-xs)]">
          <div className="border-b border-[var(--premium-border)] px-5 py-3">
            <h3 className="font-semibold text-[var(--text-primary)]">{t("sec_timeline")}</h3>
          </div>
          <div className="px-5 py-4">
            <Timeline order={order} />
          </div>
        </div>

        <OrderActivity orderId={order.id} />
      </div>

      {resolveReason && (
        <ResolveDialog
          open
          order={order}
          reason={resolveReason}
          onClose={() => setResolveReason(null)}
        />
      )}
    </div>
  );
}

interface LineProps {
  label: string;
  value: number;
  sign?: "" | "−" | "+";
  testId: string;
  currency?: string;
  danger?: boolean;
  sub?: boolean;
}

function Line({
  label,
  value,
  sign = "",
  testId,
  currency,
  danger = false,
  sub = false,
}: LineProps): JSX.Element {
  return (
    <div
      className={`flex items-center justify-between ${sub ? "pl-4 text-xs text-[var(--text-subtle)]" : ""} ${danger ? "text-[var(--intent-danger-fg)]" : ""}`}
    >
      <span className={sub || danger ? "" : "text-[var(--text-subtle)]"}>{label}</span>
      <span className="font-mono" data-testid={testId}>
        {sign ? `${sign} ` : ""}
        {money(value, currency)}
      </span>
    </div>
  );
}
